import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import { useTenant } from '../Context/TenantContext';
import LockedPopup from '../components/LockedPopup1';
import launchpadData from './launchpadData';

import { useSubscription } from '../hooks/useSubscription';

const QUESTIONS_PER_PAGE = 25;

const TopicContent = ({ mode = "learner" }) => {
  const { track, subtopic } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { tenant } = useTenant();
  const { canAccessFeature, isLoaded } = useSubscription();

  const navigate = useNavigate();

  const isTenantHost = window.location.hostname !== 'theskillgenie.com' && 
                      window.location.hostname !== 'www.theskillgenie.com' && 
                      window.location.hostname !== 'localhost' &&
                      window.location.hostname !== '127.0.0.1';
                      
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isCollegeUser = user?.role === 'student' && (user?.tenant || user?.college_id || user?.institution_id);

  const isTenantMode = mode === "tenant" || !!tenant || isTenantHost || isCollegeUser;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user?.userId || user?.user_id;

        const res = await axios.get(`${API_BASE_URL}/sgl/${track}/subtopic/${encodeURIComponent(subtopic)}`);
        const allLevels = res.data || {};
        const allQuestions = Object.values(allLevels).flat();
        setQuestions(allQuestions);
      } catch (err) {
        setError('❌ Failed to load questions.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [track, subtopic]);

  const getTopicIndex = () => {
    // In Launchpad, we need to know the index of the topic within the track.
    // This requires the track list. For now, let's use a similar approach to TechnologyDetail.
    const trackInfo = launchpadData.find(t => t.table === track);
    if (trackInfo) {
      return trackInfo.topics.findIndex(t => t.toLowerCase() === subtopic.toLowerCase());
    }
    return 999;
  };

  const hasAccess = isTenantMode || canAccessFeature('LAUNCHPAD_MCQ', { techIndex: getTopicIndex() });

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const visibleQuestions = questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  if (loading) return <div className="text-white p-4">Loading...</div>;
  if (error) return <div className="text-red-500 p-4">{error}</div>;

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-bgMain flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-bgSection p-10 rounded-3xl border border-primary/30 shadow-2xl max-w-lg">
           <div className="text-6xl mb-6">🔒</div>
           <h2 className="text-3xl font-bold text-white mb-4">Content Locked</h2>
           <p className="text-gray-400 mb-8">
             Free users can access only the first 5 topics. Upgrade to premium for unlimited access to all Launchpad MCQs.
           </p>
           <button 
             onClick={() => navigate('/subscription-plans')}
             className="bg-primary text-white font-bold py-3 px-8 rounded-xl hover:bg-secondary transition-all"
           >
             Upgrade Now
           </button>
        </div>
      </div>
    );
  }

 return (
 <div className="p-6 bg-bgSection text-body min-h-screen">
 <h1 className="text-2xl font-bold mb-4">{decodeURIComponent(subtopic)}</h1>

 {visibleQuestions.map((q, idx) => (
 <div key={q.id || idx} className="mb-4 bg-bgSection border border-gray-700 p-4 rounded-md">
 <p className="text-primary font-semibold">Q{startIndex + idx + 1}: {q.question}</p>
 <ul className="mt-2 pl-5 list-disc space-y-1 text-gray-200">
 <li>A. {q.option_a}</li>
 <li>B. {q.option_b}</li>
 <li>C. {q.option_c}</li>
 <li>D. {q.option_d}</li>
 </ul>
 </div>
 ))}

 {/* Pagination */}
 <div className="flex justify-center items-center gap-4 mt-8">
 <button
 onClick={() => goToPage(currentPage - 1)}
 disabled={currentPage === 1}
 className="px-4 py-2 bg-secondary text-black rounded hover:bg-primary disabled:opacity-40"
 >
 Prev
 </button>

 <span className="text-white">
 Page {currentPage} of {totalPages}
 </span>

 <button
 onClick={() => goToPage(currentPage + 1)}
 disabled={currentPage === totalPages}
 className="px-4 py-2 bg-secondary text-black rounded hover:bg-primary disabled:opacity-40"
 >
 Next
 </button>
 </div>
 </div>
 );
};

export default TopicContent;



